// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ball impacts, as the presentation layer sees them (M3-24).
//
// The other end of `World.contactTap`, and the sibling of `PresentationEvents`. Two feeds
// rather than one because they carry two different things:
//
//   * An **event** is a switch closing. It is the machine's own vocabulary — the channel the
//     rule engine reads — and there are perhaps fifty a second.
//   * A **contact** is a ball touching something. No machine has a switch under a rubber, no
//     rulesheet may see one, and the solver resolves thousands of them a second.
//
// Sharing a feed would mean giving contacts an event type code, which is the first step toward
// a rule that fires on one. Sharing a *ring* is safe and is what `FeedRing` is for.
//
// Its own capacity, too. A frame at 30 fps covers thirty-three simulation steps, and a
// six-ball pile-up against a slingshot's rubber can put a report in most of them; the events
// feed's two hundred and fifty-six would be four frames of headroom rather than the twenty a
// tab that has just been unhidden actually needs.

import type { ContactTap } from '@core/sim/contact'
import { FeedRing } from './feedRing'

/**
 * Contacts held before the oldest is overwritten.
 *
 * A second of the worst case: `CONTACT_REPORT_SPEED` already discards everything below 8 cm/s,
 * which is most of what a ball rolling along a rail produces, so what is left is transients.
 */
export const CONTACT_CAPACITY = 512

/** What a consumer is handed for each contact, in the order the solver resolved them. */
export type ContactVisitor = (
  ball: number,
  surface: number,
  speed: number,
  step: number,
) => void

export class PresentationContacts {
  private readonly ring: FeedRing
  private readonly balls: Int32Array
  private readonly surfaces: Int32Array
  private readonly speeds: Float64Array
  private readonly steps: Float64Array

  constructor(capacity: number = CONTACT_CAPACITY) {
    this.ring = new FeedRing(capacity)
    const n = this.ring.capacity
    this.balls = new Int32Array(n)
    this.surfaces = new Int32Array(n)
    this.speeds = new Float64Array(n)
    this.steps = new Float64Array(n)
  }

  /**
   * Install this on a world.
   *
   * A bound arrow, so `world.contactTap = contacts.record` works without the caller having to
   * think about `this` — a method reference here would record into nothing, and a machine that
   * records into nothing is indistinguishable from a machine nothing ever touches.
   */
  readonly record: ContactTap = (ball, surface, speed, step) => {
    const slot = this.ring.claim()
    this.balls[slot] = ball
    this.surfaces[slot] = surface
    this.speeds[slot] = speed
    this.steps[slot] = step
  }

  /** The cursor of a consumer that has seen everything. Pass to `read` next time. */
  get cursor(): number {
    return this.ring.cursor
  }

  /** Contacts recorded since the feed was created, including any already overwritten. */
  get recorded(): number {
    return this.ring.recorded
  }

  /** Contacts overwritten before anybody read them. A diagnostic, never a correction. */
  get dropped(): number {
    return this.ring.dropped
  }

  /** Visit everything recorded since `cursor` and return the new cursor. */
  read(cursor: number, visit: ContactVisitor): number {
    const end = this.ring.cursor
    for (let i = this.ring.begin(cursor); i < end; i++) {
      const slot = this.ring.slotOf(i)
      visit(this.balls[slot]!, this.surfaces[slot]!, this.speeds[slot]!, this.steps[slot]!)
    }
    return end
  }

  /** Forget everything, as when the machine is rebuilt. */
  clear(): void {
    this.ring.clear()
  }
}
